/**
 * @fileoverview Centralized utility module for managing filter-related localStorage operations.
 *
 * Three-key system to separate API defaults from user preferences:
 * 1. RESEARCH_FILTERS_LOCAL_STORAGE_KEY - API defaults (refreshed every 24 hours)
 * 2. USER_PREFERENCE_FILTERS - User's actual filter selections (persists across days)
 * 3. FILTERS_TIMESTAMP - Timestamp for 24-hour refresh check (milliseconds since epoch)
 *
 * @module filterLocalStorage
 * @see {@link docs/prd/filter-localstorage-fix-prd.md} for detailed PRD
 */

import {
  FILTERS_TIMESTAMP,
  RESEARCH_FILTERS_LOCAL_STORAGE_KEY,
  USER_PREFERENCE_FILTERS,
} from '@/lib/config';

import type {
  FilterItem,
  Filters,
  StringFilterItem,
} from '@/helpers/functions';

import { cleanFilters } from '@/helpers/functions';
import { invalidateSearchCache } from '@/lib/redux/slices/search';
import type {
  ReleaseDateRangeInterface,
  YearsRangeInterface,
} from '@/types/api-types';

// ============================================================================
// MIGRATION & CLEANUP OPERATIONS
// ============================================================================

/**
 * Removes legacy localStorage keys that are no longer used.
 * Should be called during initialization to clean up old data.
 */
export function removeLegacyKeys(): void {
  try {
    if (typeof window === 'undefined') return;

    // Remove the old 'research_filters' key (without _v1 suffix)
    const legacyKey = 'research_filters';
    if (sessionStorage.getItem(legacyKey) !== null) {
      sessionStorage.removeItem(legacyKey);
    }
  } catch (error) {
    console.error('Error removing legacy keys:', error);
  }
}

// ============================================================================
// SAVE OPERATIONS
// ============================================================================

/**
 * Saves user filter preferences to localStorage.
 * Should be called whenever the user explicitly changes any filter setting.
 *
 * @param {Filters} filters - The complete filter state to save
 */
export function saveUserPreferences(filters: Filters): void {
  try {
    if (typeof window === 'undefined') return;

    const cleaned = cleanFilters(filters);
    window.sessionStorage.setItem(
      USER_PREFERENCE_FILTERS,
      JSON.stringify(cleaned)
    );

    updateFiltersTimestamp();

    // Invalidate search cache since filters changed
    invalidateSearchCache();
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.error(
        '❌ LocalStorage quota exceeded while saving user preferences'
      );
      try {
        clearAllFilterStorage();
        window.sessionStorage.setItem(
          USER_PREFERENCE_FILTERS,
          JSON.stringify(cleanFilters(filters))
        );
      } catch (retryError) {
        console.error(
          '❌ Failed to save user preferences even after clearing:',
          retryError
        );
      }
    } else {
      console.error('❌ Error saving user preferences:', error);
    }
  }
}

/**
 * Saves API default filters to localStorage.
 * Should be called after successful API fetch.
 * Always pair with updateFiltersTimestamp() call.
 *
 * @param {Filters} filters - The API default filters to save
 */
export function saveApiDefaults(filters: Filters): void {
  try {
    if (typeof window === 'undefined') return;

    const cleaned = cleanFilters(filters);
    window.sessionStorage.setItem(
      RESEARCH_FILTERS_LOCAL_STORAGE_KEY,
      JSON.stringify(cleaned)
    );
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.error('❌ LocalStorage quota exceeded while saving API defaults');
      try {
        clearAllFilterStorage();
        window.sessionStorage.setItem(
          RESEARCH_FILTERS_LOCAL_STORAGE_KEY,
          JSON.stringify(cleanFilters(filters))
        );
      } catch (retryError) {
        console.error(
          '❌ Failed to save API defaults even after clearing:',
          retryError
        );
      }
    } else {
      console.error('❌ Error saving API defaults:', error);
    }
  }
}

/**
 * Updates the timestamp for when filters were last refreshed from the API.
 * Always call after saveApiDefaults().
 */
export function updateFiltersTimestamp(): void {
  try {
    if (typeof window === 'undefined') return;

    const timestamp = Date.now().toString();
    window.sessionStorage.setItem(FILTERS_TIMESTAMP, timestamp);
  } catch (error) {
    console.error('❌ Error updating filters timestamp:', error);
  }
}

// ============================================================================
// LOAD OPERATIONS
// ============================================================================

/**
 * Loads user filter preferences from localStorage.
 * Returns null if no preferences exist or if validation fails.
 *
 * @returns {Filters | null} The user's filter preferences, or null if none exist
 */
export function loadUserPreferences(): Filters | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const stored = sessionStorage.getItem(USER_PREFERENCE_FILTERS);
    if (!stored) {
      return null;
    }

    const parsed: unknown = JSON.parse(stored);
    const validated = validateFilters(parsed);
    if (!validated) {
      console.warn(
        '[loadUserPreferences] ⚠️ Validation failed - clearing corrupted data'
      );
      sessionStorage.removeItem(USER_PREFERENCE_FILTERS);
      return null;
    }

    return validated;
  } catch (error) {
    console.error(
      '[loadUserPreferences] ❌ Error loading user preferences:',
      error
    );
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(USER_PREFERENCE_FILTERS);
    }
    return null;
  }
}

/**
 * Loads API default filters from localStorage.
 * Returns null if no defaults exist or if validation fails.
 *
 * @returns {Filters | null} The cached API defaults, or null if none exist
 */
export function loadApiDefaults(): Filters | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const stored = sessionStorage.getItem(RESEARCH_FILTERS_LOCAL_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed: unknown = JSON.parse(stored);

    // Check for old format and clear storage if found
    if (isOldFormat(parsed)) {
      console.warn(
        '[loadApiDefaults] ⚠️ Old format detected - clearing to force fresh API fetch'
      );
      clearAllFilterStorage();
      return null;
    }

    const validated = validateFilters(parsed);
    if (!validated) {
      console.warn(
        '[loadApiDefaults] ⚠️ Validation failed - clearing corrupted data'
      );
      sessionStorage.removeItem(RESEARCH_FILTERS_LOCAL_STORAGE_KEY);
      return null;
    }

    return validated;
  } catch (error) {
    console.error('[loadApiDefaults] ❌ Error loading API defaults:', error);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(RESEARCH_FILTERS_LOCAL_STORAGE_KEY);
    }
    return null;
  }
}

/**
 * Loads the timestamp of when filters were last refreshed from the API.
 *
 * @returns {number | null} The timestamp in milliseconds, or null if not found
 */
function loadFiltersTimestamp(): number | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const stored = sessionStorage.getItem(FILTERS_TIMESTAMP);
    if (!stored) {
      return null;
    }

    const timestamp = Number.parseInt(stored, 10);
    if (Number.isNaN(timestamp)) {
      console.error(
        '[loadFiltersTimestamp] ❌ Invalid timestamp format:',
        stored
      );
      return null;
    }

    return timestamp;
  } catch (error) {
    console.error('[loadFiltersTimestamp] ❌ Error loading timestamp:', error);
    return null;
  }
}

/**
 * Determines if filters should be refreshed from the API.
 * Returns true if 24 hours have elapsed since the last API fetch.
 *
 * @returns {boolean} True if filters should be refreshed (≥24 hours elapsed)
 */
export function shouldRefreshFilters(): boolean {
  const timestamp = loadFiltersTimestamp();

  if (timestamp === null) {
    return true;
  }

  const now = Date.now();
  const hoursElapsed = (now - timestamp) / (1000 * 60 * 60);
  const needsRefresh = hoursElapsed >= 24;

  return needsRefresh;
}

// ============================================================================
// MERGE OPERATIONS
// ============================================================================

/**
 * Merges user preferences with API defaults to create the effective filter state.
 * User preferences take precedence over API defaults.
 *
 * @param {Partial<Filters>} userPrefs - User's saved filter preferences (may be partial)
 * @param {Filters} apiDefaults - Complete API default filter state
 * @returns {Filters} The merged filter state with user preferences overriding defaults
 */
export function mergeFiltersWithDefaults(
  userPrefs: Partial<Filters>,
  apiDefaults: Filters
): Filters {
  try {
    const merged: Filters = {
      searchDecisions: userPrefs.searchDecisions ?? apiDefaults.searchDecisions,
      searchLegislation:
        userPrefs.searchLegislation ?? apiDefaults.searchLegislation,
      decisions: {
        ...apiDefaults.decisions,
        ...userPrefs.decisions,
      },
      legislation: {
        ...apiDefaults.legislation,
        ...userPrefs.legislation,
      },
    };

    return merged;
  } catch (error) {
    console.error(
      '[mergeFiltersWithDefaults] ❌ Error merging filters, returning defaults:',
      error
    );
    return apiDefaults;
  }
}

/**
 * Gets the effective filters by loading and merging both stored filter sources.
 * Returns null if no defaults are available.
 *
 * @returns {Filters | null} The effective merged filters, or null if no defaults exist
 */
export function getEffectiveFilters(): Filters | null {
  try {
    const apiDefaults = loadApiDefaults();
    if (!apiDefaults) {
      return null;
    }

    const userPrefs = loadUserPreferences();
    if (!userPrefs) {
      return apiDefaults;
    }

    const merged = mergeFiltersWithDefaults(userPrefs, apiDefaults);

    return merged;
  } catch (error) {
    console.error(
      '[getEffectiveFilters] ❌ Error getting effective filters:',
      error
    );
    return null;
  }
}

// ============================================================================
// DELETE OPERATIONS
// ============================================================================

/**
 * Clears all filter-related data from localStorage.
 * Forces a fresh API fetch on next load. Used during logout or format migration.
 */
function clearAllFilterStorage(): void {
  try {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(RESEARCH_FILTERS_LOCAL_STORAGE_KEY);
    sessionStorage.removeItem(USER_PREFERENCE_FILTERS);
    sessionStorage.removeItem(FILTERS_TIMESTAMP);
    sessionStorage.removeItem('legislationActiveOnly');
    sessionStorage.removeItem('research_filters'); // Legacy key without _v1
  } catch (error) {
    console.error('Error clearing all filter storage:', error);
  }
}

// ============================================================================
// VALIDATION OPERATIONS
// ============================================================================

/**
 * Type guard to check if an item is a valid FilterItem (number ID).
 */
function isFilterItem(item: unknown): item is FilterItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    'name' in item &&
    typeof (item as FilterItem).id === 'number' &&
    typeof (item as FilterItem).name === 'string'
  );
}

/**
 * Type guard to check if an item is a valid StringFilterItem (string ID).
 */
function isStringFilterItem(item: unknown): item is StringFilterItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    'name' in item &&
    typeof (item as StringFilterItem).id === 'string' &&
    typeof (item as StringFilterItem).name === 'string'
  );
}

/**
 * Result of validating a single field or section.
 *
 * `ok: false` means the value was present but malformed — the caller must
 * reject the whole blob, mirroring the original all-or-nothing semantics.
 */
type Validated<T> = { ok: true; value: T } | { ok: false };

const INVALID: { ok: false } = { ok: false };

/** Validates a field that must be a boolean. */
function validateBooleanField(
  value: unknown,
  label: string
): Validated<boolean> {
  if (typeof value !== 'boolean') {
    console.warn(`[validateFilters] ⚠️ ${label} is not a boolean`);
    return INVALID;
  }
  return { ok: true, value };
}

/** Validates a field that must be a string. */
function validateStringField(value: unknown, label: string): Validated<string> {
  if (typeof value !== 'string') {
    console.warn(`[validateFilters] ⚠️ ${label} is not a string`);
    return INVALID;
  }
  return { ok: true, value };
}

/** Validates a field that must be a `string[]` (every member a string). */
function validateStringArrayField(
  value: unknown,
  label: string
): Validated<string[]> {
  if (!Array.isArray(value)) {
    console.warn(`[validateFilters] ⚠️ ${label} is not an array`);
    return INVALID;
  }
  const validCodes = value.filter((v): v is string => typeof v === 'string');
  if (validCodes.length !== value.length) {
    console.warn(`[validateFilters] ⚠️ ${label} has non-string items`);
    return INVALID;
  }
  return { ok: true, value: validCodes };
}

/**
 * Validates a field that must be an array whose members all satisfy `guard`.
 * An empty array is valid and yields an empty array.
 */
function validateItemArrayField<T>(
  value: unknown,
  label: string,
  guard: (item: unknown) => item is T
): Validated<T[]> {
  if (!Array.isArray(value)) {
    console.warn(`[validateFilters] ⚠️ ${label} is not an array`);
    return INVALID;
  }
  const validItems = value.filter(guard);
  if (validItems.length !== value.length) {
    console.warn(`[validateFilters] ⚠️ ${label} has invalid items`);
    return INVALID;
  }
  return { ok: true, value: validItems };
}

/**
 * Validates a date-range object carrying two required string keys.
 * Used for both `legislation.yearRange` and `decisions.releaseDateRange`.
 */
function validateDateRangeField<T>(
  value: unknown,
  label: string,
  earliestKey: string,
  latestKey: string
): Validated<T> {
  const range = value as Record<string, unknown>;
  if (
    typeof range !== 'object' ||
    range === null ||
    typeof range[earliestKey] !== 'string' ||
    typeof range[latestKey] !== 'string'
  ) {
    console.warn(`[validateFilters] ⚠️ ${label} is invalid`);
    return INVALID;
  }
  return { ok: true, value: range as unknown as T };
}

/** Validates one field of a section, given the raw value and its label. */
type FieldValidator = (value: unknown, label: string) => Validated<unknown>;

/**
 * Validates one section (`legislation` / `decisions`) against a table of
 * per-field validators.
 *
 * An absent section yields `{}` — matching the original, which seeded
 * `result.legislation` / `result.decisions` with `{}` and only overwrote them
 * when the key was present. Fields absent from the input are skipped; a field
 * that is present but malformed rejects the entire blob.
 */
function validateSection(
  raw: unknown,
  sectionLabel: string,
  validators: Record<string, FieldValidator>
): Validated<Record<string, unknown>> {
  if (raw === undefined) {
    return { ok: true, value: {} };
  }
  // NOTE: an array passes this check, as it did originally — arrays are
  // objects. Preserved deliberately rather than tightened here.
  if (typeof raw !== 'object' || raw === null) {
    console.warn(`[validateFilters] ⚠️ ${sectionLabel} is not an object`);
    return INVALID;
  }

  const source = raw as Record<string, unknown>;
  const validated: Record<string, unknown> = {};

  for (const [field, validate] of Object.entries(validators)) {
    if (source[field] === undefined) {
      continue;
    }
    const result = validate(source[field], `${sectionLabel}.${field}`);
    if (!result.ok) {
      return INVALID;
    }
    validated[field] = result.value;
  }

  return { ok: true, value: validated };
}

/**
 * Per-field validators for `filters.legislation`.
 *
 * Insertion order is the validation order, and therefore decides which warning
 * fires first when several fields are malformed — kept identical to the
 * original sequence of `if` blocks.
 */
const LEGISLATION_FIELD_VALIDATORS: Record<string, FieldValidator> = {
  legislationTypes: (value, label) =>
    validateItemArrayField(value, label, isFilterItem),
  yearRange: (value, label) =>
    validateDateRangeField<YearsRangeInterface>(
      value,
      label,
      'earliest_publication_date',
      'latest_publication_date'
    ),
  activeOnly: validateBooleanField,
  legislationInForceOnly: validateBooleanField,
  effectiveAsOf: validateStringField,
  jurisdictions: validateStringArrayField,
};

/**
 * Per-field validators for `filters.decisions`.
 *
 * `courtSelection` (issue #3724 phase 3) is a flat `string[]` mixing axis
 * tokens (`ustavny-sud-sr`, `krajsky-sud`, …) with individual-court tokens
 * (`court:42`), so it validates as a plain string array like
 * `institutionalCourts`.
 */
const DECISION_FIELD_VALIDATORS: Record<string, FieldValidator> = {
  areas: (value, label) => validateItemArrayField(value, label, isFilterItem),
  courts: (value, label) => validateItemArrayField(value, label, isFilterItem),
  judges: (value, label) => validateItemArrayField(value, label, isFilterItem),
  natures: (value, label) => validateItemArrayField(value, label, isFilterItem),
  subAreas: (value, label) =>
    validateItemArrayField(value, label, isFilterItem),
  caseLegislationTypes: (value, label) =>
    validateItemArrayField(value, label, isStringFilterItem),
  decisionTypes: (value, label) =>
    validateItemArrayField(value, label, isStringFilterItem),
  releaseDateRange: (value, label) =>
    validateDateRangeField<ReleaseDateRangeInterface>(
      value,
      label,
      'earliest_release_date',
      'latest_release_date'
    ),
  jurisdictions: validateStringArrayField,
  institutionalCourts: validateStringArrayField,
  publishedOnly: validateBooleanField,
  courtSelection: validateStringArrayField,
};

/**
 * Validates that data conforms to the Filters type structure.
 * Performs runtime type validation to ensure localStorage data integrity.
 *
 * @param {unknown} data - The data to validate (typically from JSON.parse)
 * @returns {Filters | null} The data as Filters if valid, null if invalid
 */
export function validateFilters(data: unknown): Filters | null {
  try {
    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
      console.warn('[validateFilters] ⚠️ Data is not an object:', typeof data);
      return null;
    }

    const obj = data as Record<string, unknown>;

    if (
      typeof obj['searchLegislation'] !== 'boolean' ||
      typeof obj['searchDecisions'] !== 'boolean'
    ) {
      console.warn(
        '[validateFilters] ⚠️ searchLegislation or searchDecisions is not a boolean'
      );
      return null;
    }

    const legislation = validateSection(
      obj['legislation'],
      'legislation',
      LEGISLATION_FIELD_VALIDATORS
    );
    if (!legislation.ok) {
      return null;
    }

    const decisions = validateSection(
      obj['decisions'],
      'decisions',
      DECISION_FIELD_VALIDATORS
    );
    if (!decisions.ok) {
      return null;
    }

    return {
      searchLegislation: obj['searchLegislation'],
      searchDecisions: obj['searchDecisions'],
      legislation: legislation.value as Filters['legislation'],
      decisions: decisions.value as Filters['decisions'],
    };
  } catch (error) {
    console.error('[validateFilters] ❌ Validation error:', error);
    return null;
  }
}

/**
 * Fields whose value is legitimately a `string[]` (not `FilterItem[]`) — the
 * old-format check skips them, otherwise their primitive first element would
 * falsely flag the entire blob as old format and wipe localStorage.
 *
 * Issue #3724 phase 3: CourtHierarchyFilter writes a flat `string[]` mixing
 * axis tokens (`ustavny-sud-sr`, `krajsky-sud`, …) and individual-court tokens
 * (`court:42`). Without its entry here, every page reload wipes the entire
 * filter blob (DATA LOSS).
 */
const PRIMITIVE_ARRAY_FIELDS = new Set([
  'jurisdictions',
  'institutionalCourts',
  'courtSelection',
]);

/**
 * Narrows a value to a plain record, or `null` when it is absent or not an
 * object. Arrays are accepted, as they were originally — `typeof []` is
 * `'object'` — so callers that must reject them check `Array.isArray` too.
 */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * True when an array carries the old format — i.e. its first member is a
 * primitive rather than a `{ id, name }` object. Non-arrays and empty arrays
 * are never old format.
 */
function isOldFormatArray(arr: unknown): boolean {
  if (!Array.isArray(arr) || arr.length === 0) {
    return false;
  }
  const firstItem = arr[0];
  return (
    typeof firstItem !== 'object' ||
    firstItem === null ||
    !('name' in firstItem)
  );
}

/** True when any non-primitive-array field of `decisions` is old format. */
function hasOldFormatDecisions(raw: unknown): boolean {
  const decisions = asRecord(raw);
  if (!decisions) {
    return false;
  }
  return Object.entries(decisions).some(
    ([key, value]) =>
      !PRIMITIVE_ARRAY_FIELDS.has(key) && isOldFormatArray(value)
  );
}

/** True when `legislation.legislationTypes` is old format. */
function hasOldFormatLegislation(raw: unknown): boolean {
  const legislation = asRecord(raw);
  if (!legislation) {
    return false;
  }
  const types = legislation['legislationTypes'];
  return Boolean(types) && isOldFormatArray(types);
}

/**
 * Detects if localStorage data is in the old format.
 * Old format: arrays contain primitives [1, 2, 3] or ["uuid1", "uuid2"]
 * New format: arrays contain objects [{id: 1, name: "Item 1"}, ...]
 *
 * @param {unknown} data - The data to check
 * @returns {boolean} True if data is in old format, false otherwise
 */
export function isOldFormat(data: unknown): boolean {
  try {
    const obj = asRecord(data);
    if (!obj || Array.isArray(data)) {
      return false;
    }
    return (
      hasOldFormatDecisions(obj['decisions']) ||
      hasOldFormatLegislation(obj['legislation'])
    );
  } catch (error) {
    console.error('[isOldFormat] ❌ Error checking format:', error);
    return false;
  }
}
